import { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Check, ChevronRight, X } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { apiError } from "../lib/api";
import { bodyRegionLabel } from "../lib/bodyRegions";
import { watchInjuryViewport } from "../lib/injuryViewport";
import BodyRegionSelector from "./BodyRegionSelector";
import "../injury-flow.css";

export const INJURY_COPY = {
  de: {
    title: "Verletzung / Problem melden", question: "Wo hast du Beschwerden oder eine Verletzung?",
    hint: "Tippe auf die betroffene Körperstelle.", selected: "Ausgewählte Stelle",
    remove: "Auswahl entfernen", description: "Beschreibe deine Beschwerden.",
    placeholder: "Was spürst du und seit wann?",
    completion: "Konntest du das Training vollständig absolvieren?", full: "Vollständig",
    sixty: "Abbruch nach 60 Min.", thirty: "Abbruch nach 30 Min.", save: "Meldung speichern",
    apply: "Übernehmen", draft: "Wird zusammen mit deinem Feedback gespeichert.",
    close: "Schließen", edit: "Angaben bearbeiten", removeReport: "Angaben entfernen",
    legacy: "Beschwerden", saving: "Wird gespeichert …",
    failed: "Die Meldung konnte nicht gespeichert werden. Bitte versuche es erneut.",
  },
  en: {
    title: "Report injury / problem", question: "Where do you feel discomfort or have an injury?",
    hint: "Tap the affected area of your body.", selected: "Selected area",
    remove: "Clear selection", description: "Describe your symptoms.",
    placeholder: "What are you feeling, and since when?",
    completion: "Were you able to complete the training session?", full: "In full",
    sixty: "Stopped after 60 min", thirty: "Stopped after 30 min", save: "Save report",
    apply: "Apply", draft: "Saved together with your feedback.",
    close: "Close", edit: "Edit details", removeReport: "Remove details",
    legacy: "Discomfort", saving: "Saving …",
    failed: "The report could not be saved. Please try again.",
  },
};

export function TrainingCompletionSelector({ value, onChange, lang = "de", disabled = false }) {
  const copy = INJURY_COPY[lang === "de" ? "de" : "en"];
  return <fieldset className="pc-injury-completion" data-testid="injury-completion" disabled={disabled}>
    <legend>{copy.completion}</legend>
    <div>{[["full", copy.full], [60, copy.sixty], [30, copy.thirty]].map(([key, label]) =>
      <button type="button" key={key} aria-pressed={value === key} onClick={() => onChange(key)}
        data-completion={key}>{label}</button>)}</div>
  </fieldset>;
}

export function InjuryDraftSummary({ value, training = false, onEdit, onRemove }) {
  const { lang } = useI18n();
  const copy = INJURY_COPY[lang === "de" ? "de" : "en"];
  return <div className="pc-injury-draft" data-testid="injury-draft-summary">
    <div><strong>{bodyRegionLabel(value.body_region_code, lang) || value.injury_area || copy.legacy}</strong>
      {value.injury_notes && <p>{value.injury_notes}</p>}
      {training && <p>{value.training_aborted ? value.abort_after_minutes === 60 ? copy.sixty : copy.thirty : copy.full}</p>}
    </div>
    <div className="pc-injury-draft__actions">
      <button className="pc-btn pc-btn-ghost" type="button" onClick={onEdit}>{copy.edit}<ChevronRight size={14} /></button>
      {onRemove && <button className="pc-btn pc-btn-ghost" type="button" onClick={onRemove} aria-label={copy.removeReport}><X size={16} /></button>}
    </div>
  </div>;
}

// The dialog owns only a local draft. A feedback subflow never calls an API.
export default function InjuryReportFlow({ contextTitle, returnFocusContextId, training = false, initialValue = {}, draft = false, onClose, onSave }) {
  const { lang } = useI18n();
  const copy = INJURY_COPY[lang === "de" ? "de" : "en"];
  const [region, setRegion] = useState(initialValue.body_region_code || null);
  const [description, setDescription] = useState(initialValue.injury_notes || "");
  const [completion, setCompletion] = useState(initialValue.training_aborted === true
    ? initialValue.abort_after_minutes : initialValue.training_aborted === false ? "full" : null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const saving = useRef(false);
  const content = useRef(null);
  const [scrollElement, setScrollElement] = useState(null);
  const closeButton = useRef(null);
  const returnFocus = useRef(document.activeElement);
  const returnContext = useRef(returnFocusContextId || document.activeElement?.closest?.('[data-testid^="today-actions-"]')?.getAttribute('data-testid'));
  const valid = Boolean(region && description.trim() && description.length <= 2000
    && (!training || ["full", 30, 60].includes(completion)));

  useEffect(() => {
    const root = document.documentElement;
    const previous = root.getAttribute("data-injury-flow-open");
    root.setAttribute("data-injury-flow-open", "true");
    const restoreKeyboard = watchInjuryViewport(content.current, scrollElement);
    return () => {
      previous === null ? root.removeAttribute("data-injury-flow-open") : root.setAttribute("data-injury-flow-open", previous);
      restoreKeyboard();
    };
  }, [scrollElement]);

  const submit = async (event) => {
    event.preventDefault();
    if (!valid || saving.current) return;
    saving.current = true; setBusy(true); setError("");
    try {
      await onSave({
        body_region_code: region, injury_notes: description.trim(),
        ...(training ? { training_aborted: completion !== "full", abort_after_minutes: completion === "full" ? null : completion } : {}),
      });
      onClose();
    } catch (err) {
      setError(apiError(err?.response?.data?.detail, copy.failed));
    } finally { saving.current = false; setBusy(false); }
  };

  return <Dialog.Root open onOpenChange={(open) => { if (!open && !saving.current) onClose(); }}>
    <Dialog.Portal>
      <Dialog.Overlay className="pc-injury-backdrop" />
      <Dialog.Content ref={content} lang={lang} className="pc-player-ui pc-injury-flow" data-testid="injury-report-flow"
        onOpenAutoFocus={(event) => { event.preventDefault(); closeButton.current?.focus(); }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          // Applying a new draft replaces its launcher with the summary.
          const liveContext = [...document.querySelectorAll('[data-testid^="today-actions-"]')]
            .find((element) => element.getAttribute('data-testid') === returnContext.current);
          const target = returnFocus.current?.isConnected && returnFocus.current !== document.body ? returnFocus.current
            : liveContext?.querySelector('[data-testid="injury-draft-summary"] button, button');
          target?.focus?.({ preventScroll: true });
        }}
        onInteractOutside={(event) => event.preventDefault()}>
        <header className="pc-injury-flow__header">
          <Dialog.Close asChild><button ref={closeButton} type="button" className="pc-btn pc-btn-ghost" disabled={busy} aria-label={copy.close}><X size={21} /></button></Dialog.Close>
          <Dialog.Title>{copy.title}</Dialog.Title><span aria-hidden="true" />
        </header>
        <form ref={setScrollElement} className="pc-injury-flow__scroll" onSubmit={submit} data-training-keyboard-restore="">
          <div className="pc-injury-flow__body">
            <div className="pc-injury-flow__intro">
              {contextTitle && <p className="pc-injury-flow__context">{contextTitle}</p>}
              <h2>{copy.question}</h2><Dialog.Description>{copy.hint}</Dialog.Description>
            </div>
            <fieldset className="pc-injury-flow__fields" disabled={busy}>
              <BodyRegionSelector value={region} onChange={(next) => { if (!saving.current) setRegion(next); }} lang={lang}
                renderSelection={({ label }) => region && <div className="pc-injury-selection" aria-live="polite" data-testid="injury-region-summary">
                <div><p>{copy.selected}</p><strong>{label}</strong></div>
                <button type="button" className="pc-btn pc-btn-ghost" aria-label={copy.remove} onClick={() => setRegion(null)}><X size={18} /></button>
              </div>} />
              <div data-injury-writing-block="">
                <label className="pc-injury-description" htmlFor="injury-description">{copy.description}
                  <textarea id="injury-description" className="pc-input" rows="4" value={description} required maxLength={2000}
                    onChange={(event) => setDescription(event.target.value)} placeholder={copy.placeholder} />
                </label>
                {training && <TrainingCompletionSelector value={completion} onChange={setCompletion} lang={lang} disabled={busy} />}
              </div>
            </fieldset>
            {draft && <p className="pc-injury-flow__draft-note">{copy.draft}</p>}
            {error && <p className="pc-injury-flow__error" role="alert">{error}</p>}
            <button type="submit" className="pc-btn pc-btn-primary pc-injury-flow__save" disabled={!valid || busy} data-testid="injury-save">
              <Check size={18} />{busy ? copy.saving : draft ? copy.apply : copy.save}
            </button>
          </div>
        </form>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>;
}
